import React, { useState, useEffect, useRef, useCallback } from "react";
import { database } from "../firebaseConfig";
import { ref, onValue, set, remove, update } from "firebase/database";
import ConfirmModal from "./ConfirmModal";
import downArrow from "../img/down.svg";
import {
  potentialProfilesPath,
  potentialLegacyPath,
  POTENTIAL_PROFILE_FIELDS,
  STRICTNESS_OPTIONS,
  LANGUAGE_OPTIONS,
  LEGACY_PROFILE_ID,
  makePotentialProfile,
  profilesFromSnapshot,
  hasIcpContent,
  potentialProfileScore,
  potentialProfileScoreTone,
  potentialProfileScoreLabel,
} from "./potentialProfiles";
import "./PotentialAnalyzerSettings.css";

const formatCreatedAt = (createdAt) => {
  if (createdAt == null || createdAt === "") return null;
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString();
};

/**
 * Potential-Analyser-Einstellungen: ein Hauptschalter plus beliebig viele
 * ICP-Profile (`settings/potentialAnalyzerList`). Jedes Profil kann in einem
 * Outreach Agent, im Bulk-Research oder als Standard für Einzelläufe gewählt
 * werden — analog zu den Guideline-Sets des List Cleaners.
 */
const PotentialAnalyzerSettings = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const [enabled, setEnabled] = useState(false);
  const [legacySettings, setLegacySettings] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [showAddProfile, setShowAddProfile] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  // Textfelder werden lokal gepuffert und erst beim Verlassen geschrieben —
  // sonst würde jeder Tastendruck einen RTDB-Write auslösen.
  const [drafts, setDrafts] = useState({});
  const [message, setMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null });

  const savedTimerRef = useRef(null);
  const messageTimerRef = useRef(null);
  const legacyLoadedRef = useRef(false);
  const profilesLoadedRef = useRef(false);
  const migrationDoneRef = useRef(false);

  const flashMessage = useCallback((text, duration = 3000) => {
    setMessage(text);
    clearTimeout(messageTimerRef.current);
    if (duration) messageTimerRef.current = setTimeout(() => setMessage(''), duration);
  }, []);

  useEffect(() => () => {
    clearTimeout(savedTimerRef.current);
    clearTimeout(messageTimerRef.current);
  }, []);

  // ── Listener: Hauptschalter/Alt-Config + Profil-Liste ──
  useEffect(() => {
    if (!userId) return undefined;
    legacyLoadedRef.current = false;
    profilesLoadedRef.current = false;
    migrationDoneRef.current = false;
    setLoading(true);

    const unsubLegacy = onValue(ref(database, potentialLegacyPath(userId)), (snapshot) => {
      const data = snapshot.exists() ? (snapshot.val() || {}) : {};
      setLegacySettings(data);
      setEnabled(data.enabled === true);
      legacyLoadedRef.current = true;
      if (profilesLoadedRef.current) setLoading(false);
    });

    const unsubProfiles = onValue(ref(database, potentialProfilesPath(userId)), (snapshot) => {
      setProfiles(profilesFromSnapshot(snapshot.exists() ? snapshot.val() : null));
      profilesLoadedRef.current = true;
      if (legacyLoadedRef.current) setLoading(false);
    }, (error) => {
      console.error('Fehler beim Laden der Potential-Profile', error);
      profilesLoadedRef.current = true;
      setLoading(false);
    });

    return () => { unsubLegacy(); unsubProfiles(); };
  }, [userId]);

  // Einmalige Übernahme der alten Einzel-Config in ein echtes Profil. Feste ID,
  // damit parallele Tabs denselben Knoten schreiben statt Duplikate anzulegen.
  // Der Alt-Knoten bleibt liegen und dient weiterhin als Fallback.
  useEffect(() => {
    if (!userId || loading || migrationDoneRef.current) return;
    if (profiles.length > 0 || !hasIcpContent(legacySettings)) return;
    migrationDoneRef.current = true;
    // Nur die ICP-Felder übernehmen — `enabled` bleibt Hauptschalter im Alt-Knoten.
    const icpFields = {};
    Object.keys(POTENTIAL_PROFILE_FIELDS).forEach((key) => {
      icpFields[key] = legacySettings[key] === undefined
        ? POTENTIAL_PROFILE_FIELDS[key]
        : legacySettings[key];
    });
    const migrated = {
      ...icpFields,
      id: LEGACY_PROFILE_ID,
      name: 'Standard-Profil',
      isDefault: true,
      createdAt: legacySettings.updatedAt || Date.now(),
    };
    set(ref(database, `${potentialProfilesPath(userId)}/${LEGACY_PROFILE_ID}`), migrated)
      .then(() => flashMessage('Deine bisherige ICP-Beschreibung wurde als "Standard-Profil" übernommen.', 5000))
      .catch((error) => console.error('Migration der Potential-Settings fehlgeschlagen:', error));
  }, [userId, loading, profiles.length, legacySettings, flashMessage]);

  // ── Persistenz ──
  const markSaved = useCallback(() => {
    setSaveStatus('saved');
    clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2400);
  }, []);

  const toggleEnabled = async () => {
    const next = !enabled;
    setEnabled(next);
    setSaveStatus('saving');
    try {
      await update(ref(database, potentialLegacyPath(userId)), { enabled: next, updatedAt: Date.now() });
      markSaved();
    } catch (error) {
      console.error('Fehler beim Speichern des Potential-Analyser-Schalters:', error);
      setEnabled(!next);
      setSaveStatus('idle');
    }
  };

  const updateProfileFields = useCallback(async (profileId, fields) => {
    setSaveStatus('saving');
    try {
      const updates = {};
      Object.entries(fields).forEach(([field, value]) => {
        updates[`${potentialProfilesPath(userId)}/${profileId}/${field}`] = value;
      });
      updates[`${potentialProfilesPath(userId)}/${profileId}/updatedAt`] = Date.now();
      await update(ref(database), updates);
      setProfiles(prev => prev.map(p => (p.id === profileId ? { ...p, ...fields } : p)));
      markSaved();
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Potential-Profils:', error);
      setSaveStatus('idle');
      flashMessage('Profil konnte nicht gespeichert werden.');
    }
  }, [userId, markSaved, flashMessage]);

  const draftValue = (profile, field) => {
    const draft = drafts[profile.id];
    if (draft && draft[field] !== undefined) return draft[field];
    const value = profile[field];
    return value === undefined || value === null ? '' : value;
  };

  const setDraft = (profileId, field, value) => {
    setDrafts(prev => ({ ...prev, [profileId]: { ...prev[profileId], [field]: value } }));
  };

  const clearDraft = (profileId, field) => {
    setDrafts(prev => {
      const forProfile = { ...prev[profileId] };
      delete forProfile[field];
      const next = { ...prev, [profileId]: forProfile };
      if (Object.keys(forProfile).length === 0) delete next[profileId];
      return next;
    });
  };

  const commitDraft = async (profileId, field, rawValue) => {
    const isNumberField = field === 'employeeMin' || field === 'employeeMax';
    const cleaned = isNumberField
      ? (String(rawValue).replace(/[^0-9]/g, '') === '' ? '' : parseInt(String(rawValue).replace(/[^0-9]/g, ''), 10))
      : rawValue;
    await updateProfileFields(profileId, { [field]: cleaned });
    clearDraft(profileId, field);
  };

  const handleAddProfile = async () => {
    if (!newProfileName.trim()) {
      flashMessage('Bitte gib dem Profil einen Namen.');
      return;
    }
    const profile = makePotentialProfile(newProfileName, { isDefault: profiles.length === 0 });
    try {
      await set(ref(database, `${potentialProfilesPath(userId)}/${profile.id}`), profile);
      setNewProfileName('');
      setShowAddProfile(false);
      setExpandedId(profile.id);
      flashMessage('Neues ICP-Profil erstellt.');
    } catch (error) {
      console.error('Fehler beim Erstellen des Potential-Profils:', error);
      flashMessage('Profil konnte nicht erstellt werden.');
    }
  };

  const handleDuplicateProfile = async (source) => {
    const copy = makePotentialProfile(`${source.name} (Kopie)`, {
      description: source.description || '',
      revenueSize: source.revenueSize || '',
      employeeMin: source.employeeMin ?? '',
      employeeMax: source.employeeMax ?? '',
      strictness: source.strictness || 'balanced',
      language: source.language || 'auto',
    });
    try {
      await set(ref(database, `${potentialProfilesPath(userId)}/${copy.id}`), copy);
      setExpandedId(copy.id);
      flashMessage('Profil dupliziert.');
    } catch (error) {
      console.error('Fehler beim Duplizieren des Potential-Profils:', error);
      flashMessage('Profil konnte nicht dupliziert werden.');
    }
  };

  const handleSetDefault = async (profileId) => {
    try {
      const updates = {};
      profiles.forEach((p) => {
        updates[`${potentialProfilesPath(userId)}/${p.id}/isDefault`] = p.id === profileId;
      });
      await update(ref(database), updates);
      setProfiles(prev => prev.map(p => ({ ...p, isDefault: p.id === profileId })));
      flashMessage('Standard-Profil gesetzt.');
    } catch (error) {
      console.error('Fehler beim Setzen des Standard-Profils:', error);
      flashMessage('Standard-Profil konnte nicht gesetzt werden.');
    }
  };

  const handleDeleteProfile = (profileId) => {
    // Ohne Profil würde der Analyser mit leerem ICP laufen und wahllos Scores
    // vergeben — deshalb bleibt mindestens eines stehen.
    if (profiles.length <= 1) {
      flashMessage('Das letzte Profil kann nicht gelöscht werden.');
      return;
    }
    setConfirmModal({
      open: true,
      title: 'Profil löschen',
      message: 'Dieses ICP-Profil wirklich löschen? Agents, die es nutzen, fallen auf das Standard-Profil zurück.',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));
        try {
          const wasDefault = profiles.find(p => p.id === profileId)?.isDefault;
          await remove(ref(database, `${potentialProfilesPath(userId)}/${profileId}`));
          const remaining = profiles.filter(p => p.id !== profileId);
          if (wasDefault && remaining[0]) {
            await set(ref(database, `${potentialProfilesPath(userId)}/${remaining[0].id}/isDefault`), true);
          }
          if (expandedId === profileId) setExpandedId(null);
          flashMessage('Profil gelöscht.');
        } catch (error) {
          console.error('Fehler beim Löschen des Potential-Profils:', error);
          flashMessage('Profil konnte nicht gelöscht werden.');
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="paSettingsLoading">
        <i className="fa fa-spinner fa-spin"></i>
      </div>
    );
  }

  return (
    <>
      <div className="paSettingsContainer">
        {/* Master enable toggle */}
        <div className={`paEnableCard ${enabled ? 'on' : ''}`}>
          <div className="paEnableIcon">
            <i className="fa fa-bullseye"></i>
          </div>
          <div className="paEnableText">
            <h4>Potential Analyser aktivieren</h4>
            <p>Bewertet jeden Kontakt von 1–10, wie gut er zu deinem Wunschkunden passt – inkl. kurzer Begründung.</p>
          </div>
          <div
            className={`paToggle ${enabled ? 'on' : ''}`}
            onClick={toggleEnabled}
            role="switch"
            aria-checked={enabled}
          >
            <div className="paToggleKnob"></div>
          </div>
        </div>

        {enabled && (
          <div className="paConfig">
            {message && <div className="paInlineMessage">{message}</div>}

            <div className="paProfilesIntro">
              <i className="fa fa-layer-group"></i>
              <span>
                Lege mehrere <strong>ICP-Profile</strong> an – z.B. eines pro Zielgruppe oder Kampagne.
                In jedem <strong>Outreach Agent</strong> und im Bulk-Research wählst du, mit welchem Profil bewertet wird.
                Einzelläufe über das Research-Menü nutzen das <strong>Standard-Profil</strong>.
              </span>
            </div>

            <div className="paProfileList">
              {profiles.map((profile) => {
                const isOpen = expandedId === profile.id;
                const score = potentialProfileScore(profile);
                const tone = potentialProfileScoreTone(score);
                const createdLabel = formatCreatedAt(profile.createdAt);
                const descValue = String(draftValue(profile, 'description'));

                return (
                  <article
                    key={profile.id}
                    className={`paProfileCard ${profile.isDefault ? 'is-default' : ''} ${isOpen ? 'is-open' : ''}`}
                  >
                    <div
                      className="paProfileHead"
                      onClick={() => setExpandedId(prev => (prev === profile.id ? null : profile.id))}
                    >
                      <div className="paProfileHeadMain">
                        <div className="paProfileTitleRow">
                          <span className="paProfileName">{profile.name || 'Unbenanntes Profil'}</span>
                          {profile.isDefault ? (
                            <span className="paProfilePill">Standard</span>
                          ) : (
                            <button
                              type="button"
                              className="paProfileDefaultBtn"
                              onClick={(e) => { e.stopPropagation(); handleSetDefault(profile.id); }}
                            >
                              Als Standard
                            </button>
                          )}
                        </div>
                        <div className="paProfileMeta">
                          {createdLabel && <span>Erstellt {createdLabel}</span>}
                          {createdLabel && <span className="paProfileMetaDot" aria-hidden="true">·</span>}
                          <span
                            className={`paProfileScore paProfileScore--${tone}`}
                            title={`Wie viel Kontext die KI aus diesem Profil bekommt (${score}/10)`}
                          >
                            <span className="paProfileScoreLabel">{potentialProfileScoreLabel(score)}</span>
                            <span className="paProfileScoreBar" aria-hidden="true">
                              <span className="paProfileScoreBarFill" style={{ width: `${(score / 10) * 100}%` }} />
                            </span>
                            <span className="paProfileScoreValue">{score}/10</span>
                          </span>
                        </div>
                      </div>
                      <span className="paProfileExpandIcon" aria-hidden="true">
                        <img
                          src={downArrow}
                          alt=""
                          style={{
                            width: 16,
                            height: 16,
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                          }}
                        />
                      </span>
                    </div>

                    {isOpen && (
                      <div className="paProfileBody">
                        {/* Name */}
                        <div className="paField">
                          <label className="paLabel">
                            <i className="fa fa-tag"></i>
                            Profilname
                          </label>
                          <input
                            type="text"
                            className="paInput"
                            placeholder="z.B. E-Commerce DACH"
                            value={String(draftValue(profile, 'name'))}
                            onChange={(e) => setDraft(profile.id, 'name', e.target.value)}
                            onBlur={(e) => commitDraft(profile.id, 'name', e.target.value)}
                            maxLength={80}
                          />
                        </div>

                        {/* ICP description */}
                        <div className="paField">
                          <label className="paLabel">
                            <i className="fa fa-wand-magic-sparkles"></i>
                            Was macht einen Kontakt für dich wertvoll?
                          </label>
                          <p className="paHint">Beschreibe deinen Wunschkunden so genau wie möglich – Branche, Profil, was du anbietest und wen du erreichen willst. Die KI nutzt das, um eine ehrliche Einschätzung zu treffen.</p>
                          <textarea
                            className="paTextarea"
                            placeholder="z.B. Wir suchen E-Commerce-Shops im DACH-Raum mit eigenem Lager, die aktiv wachsen. Ideal sind inhabergeführte Brands mit gutem Sortiment, die Performance-Marketing brauchen. Zu große Konzerne oder reine Dropshipper sind kein Fit."
                            value={descValue}
                            onChange={(e) => setDraft(profile.id, 'description', e.target.value)}
                            onBlur={(e) => commitDraft(profile.id, 'description', e.target.value)}
                            maxLength={1500}
                            rows={5}
                          />
                          <div className="paCharCount">{descValue.length}/1500</div>
                        </div>

                        {/* Revenue size */}
                        <div className="paField">
                          <label className="paLabel">
                            <i className="fa fa-euro-sign"></i>
                            Ungefähre Unternehmensgröße (Umsatz)
                          </label>
                          <p className="paHint">Grobe Umsatz-Spanne deines Wunschkunden – hilft der KI, zu kleine oder zu große Firmen sofort zu erkennen.</p>
                          <input
                            type="text"
                            className="paInput"
                            placeholder="z.B. 1–10 Mio. € Jahresumsatz"
                            value={String(draftValue(profile, 'revenueSize'))}
                            onChange={(e) => setDraft(profile.id, 'revenueSize', e.target.value)}
                            onBlur={(e) => commitDraft(profile.id, 'revenueSize', e.target.value)}
                            maxLength={200}
                          />
                        </div>

                        {/* Employee range */}
                        <div className="paField">
                          <label className="paLabel">
                            <i className="fa fa-users"></i>
                            Gewünschte Mitarbeiteranzahl
                          </label>
                          <p className="paHint">Von – bis. Lass ein Feld leer, wenn es keine Grenze gibt.</p>
                          <div className="paRangeRow">
                            <div className="paRangeInput">
                              <span className="paRangePrefix">Von</span>
                              <input
                                type="text"
                                inputMode="numeric"
                                className="paInput paInputNumber"
                                placeholder="10"
                                value={String(draftValue(profile, 'employeeMin'))}
                                onChange={(e) => setDraft(profile.id, 'employeeMin', e.target.value.replace(/[^0-9]/g, ''))}
                                onBlur={(e) => commitDraft(profile.id, 'employeeMin', e.target.value)}
                              />
                            </div>
                            <span className="paRangeDash">–</span>
                            <div className="paRangeInput">
                              <span className="paRangePrefix">Bis</span>
                              <input
                                type="text"
                                inputMode="numeric"
                                className="paInput paInputNumber"
                                placeholder="200"
                                value={String(draftValue(profile, 'employeeMax'))}
                                onChange={(e) => setDraft(profile.id, 'employeeMax', e.target.value.replace(/[^0-9]/g, ''))}
                                onBlur={(e) => commitDraft(profile.id, 'employeeMax', e.target.value)}
                              />
                            </div>
                            <span className="paRangeSuffix">Mitarbeiter</span>
                          </div>
                        </div>

                        {/* Strictness */}
                        <div className="paField">
                          <label className="paLabel">
                            <i className="fa fa-sliders"></i>
                            Wie streng soll die KI bewerten?
                          </label>
                          <p className="paHint">Bestimmt, wie genau ein Kontakt passen muss, um eine hohe Zahl zu bekommen.</p>
                          <div className="paStrictnessGrid">
                            {STRICTNESS_OPTIONS.map(opt => (
                              <button
                                key={opt.id}
                                type="button"
                                className={`paStrictnessOption ${profile.strictness === opt.id ? 'active' : ''}`}
                                onClick={() => updateProfileFields(profile.id, { strictness: opt.id })}
                              >
                                <i className={`fa ${opt.icon}`}></i>
                                <span className="paStrictnessLabel">{opt.label}</span>
                                <span className="paStrictnessDesc">{opt.desc}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Language */}
                        <div className="paField">
                          <label className="paLabel">
                            <i className="fa fa-globe"></i>
                            Sprache der Begründung
                          </label>
                          <div className="paLangOptions">
                            {LANGUAGE_OPTIONS.map(lang => (
                              <button
                                key={lang.id}
                                type="button"
                                className={`paLangOption ${profile.language === lang.id ? 'active' : ''}`}
                                onClick={() => updateProfileFields(profile.id, { language: lang.id })}
                              >
                                {lang.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="paProfileFoot">
                          <button
                            type="button"
                            className="paProfileDuplicateBtn"
                            onClick={() => handleDuplicateProfile(profile)}
                          >
                            <i className="fa fa-clone"></i> Duplizieren
                          </button>
                          {profiles.length > 1 && (
                            <button
                              type="button"
                              className="paProfileDeleteBtn"
                              onClick={() => handleDeleteProfile(profile.id)}
                            >
                              Profil löschen
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>

            {profiles.length === 0 && (
              <div className="paProfileEmpty">
                <i className="fa fa-bullseye"></i>
                <span>Noch kein ICP-Profil. Lege eines an, damit die KI weiß, wen du suchst.</span>
              </div>
            )}

            {showAddProfile ? (
              <div className="paAddProfileRow">
                <input
                  type="text"
                  className="paInput"
                  value={newProfileName}
                  onChange={(e) => setNewProfileName(e.target.value)}
                  placeholder="Name für das neue ICP-Profil…"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newProfileName.trim()) handleAddProfile();
                    if (e.key === 'Escape') { setShowAddProfile(false); setNewProfileName(''); }
                  }}
                />
                <button className="paAddProfileCreate" onClick={handleAddProfile} disabled={!newProfileName.trim()}>
                  Erstellen
                </button>
                <button
                  className="paAddProfileCancel"
                  onClick={() => { setShowAddProfile(false); setNewProfileName(''); }}
                >
                  ×
                </button>
              </div>
            ) : (
              <button className="paAddProfileBtn" onClick={() => setShowAddProfile(true)}>
                <span className="paAddProfileIcon">+</span>
                ICP-Profil hinzufügen
              </button>
            )}

            <div className="paInfoBox">
              <i className="fa fa-circle-info"></i>
              <span>
                Der Score (z.B. <strong>8/10</strong>) erscheint anstelle der Zeilennummer in der Tabelle. Die kurze Begründung wird automatisch unten an die Beschreibung angehängt. Du startest die Analyse pro Kontakt über das <strong>Research</strong>-Menü, per Bulk-Aktion oder als Schritt in einem <strong>Outreach Agent</strong>.
              </span>
            </div>
          </div>
        )}

        <div className={`paAutoSaveIndicator ${saveStatus}`}>
          {saveStatus === 'saving' && (<><span className="paSaveSpinner"></span><span>Speichern…</span></>)}
          {saveStatus === 'saved' && (<><i className="fa fa-check"></i><span>Gespeichert</span></>)}
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default PotentialAnalyzerSettings;
